import { observeImmersiveHeader } from '@theme/section-immersive-header';

/**
 * "Snap into focus" for section.liquid sections (the snap setting, full-screen
 * height only). Two behaviours, both gated to the breakpoint the snap applies on
 * (.section--snap-desktop -> >= 750px, .section--snap-mobile -> <= 749px):
 *
 *   1. Hide the sticky site header while the section fills the viewport (shared
 *      @theme/section-immersive-header, same as scroll-story / animated-steps).
 *   2. Gently snap the section to the top of the viewport once scrolling settles
 *      and the section is the one dominating the screen.
 *
 * The snap is done in JS (not CSS root scroll-snap) and only ever scrolls toward
 * THIS element — so it can never pull unrelated sections around, which is what
 * happened when scroll-snap-type lived on <html> (descendant snap targets in
 * other sections' carousels leaked into the page scroller).
 */
const SELECTOR = '.section--snap-desktop, .section--snap-mobile';
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @param {HTMLElement} el */
function bind(el) {
  if (el.dataset.snapBound) return;
  el.dataset.snapBound = 'true';

  const queries = [];
  if (el.classList.contains('section--snap-desktop')) queries.push('(min-width: 750px)');
  if (el.classList.contains('section--snap-mobile')) queries.push('(max-width: 749px)');
  if (queries.length === 0) return;

  const mqls = queries.map((q) => window.matchMedia(q));
  const breakpointActive = () => mqls.some((m) => m.matches);

  // 1) Header hide — observe only while the matching breakpoint is active.
  let disconnectHeader = null;
  const syncHeader = () => {
    if (breakpointActive() && !disconnectHeader) {
      disconnectHeader = observeImmersiveHeader(el);
    } else if (!breakpointActive() && disconnectHeader) {
      disconnectHeader();
      disconnectHeader = null;
    }
  };
  mqls.forEach((m) => m.addEventListener('change', syncHeader));
  syncHeader();

  // 2) Snap into focus — skip entirely under reduced motion.
  if (REDUCED) return;

  let settling = false;
  let debounce = null;

  // Only snap when the section is already nearly aligned (within this many px of
  // the top). Tight band = it just finishes the last sliver of a deliberate
  // scroll-to; it never yanks the page when you're only part-way onto it.
  const SNAP_THRESHOLD = 100;

  const snap = () => {
    if (!breakpointActive() || settling) return;
    const rect = el.getBoundingClientRect();
    if (Math.abs(rect.top) <= 2) return; // already aligned
    if (Math.abs(rect.top) > SNAP_THRESHOLD) return; // too far off — leave it
    settling = true;
    window.scrollTo({ top: window.scrollY + rect.top, behavior: 'smooth' });
    // Release once the smooth scroll has settled so our own scrolling doesn't
    // re-trigger the snap.
    setTimeout(() => {
      settling = false;
    }, 700);
  };

  // Prefer the native scrollend event; fall back to a debounced scroll.
  const hasScrollEnd = 'onscrollend' in window;
  const onScrollEnd = () => snap();
  const onScroll = () => {
    if (hasScrollEnd) return;
    clearTimeout(debounce);
    debounce = setTimeout(snap, 140);
  };
  window.addEventListener('scrollend', onScrollEnd);
  window.addEventListener('scroll', onScroll, { passive: true });
}

/** @param {ParentNode} root */
function init(root) {
  root.querySelectorAll(SELECTOR).forEach((el) => bind(/** @type {HTMLElement} */(el)));
}

init(document);

// Re-bind sections added/reloaded in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (target.matches?.(SELECTOR)) bind(target);
  init(target);
});
